import { useEffect, useState, useCallback } from 'react';
import { config } from '../utils/config';
import AskAIButton from '../components/chat/AskAIButton';

const About = () => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const staticUrl = config.staticUrl;

  const setupAudioNarration = useCallback(() => {
    const audio = document.getElementById('about-narration') as HTMLAudioElement | null;
    const playIcon = document.getElementById('play-icon');
    const pauseIcon = document.getElementById('pause-icon');
    const btnText = document.getElementById('audio-btn-text');
    if (!audio || !playIcon || !pauseIcon || !btnText) return;

    (window as unknown as Record<string, unknown>).toggleAudioNarration = () => {
      if (audio.paused) {
        audio.play();
        playIcon.classList.add('hidden');
        pauseIcon.classList.remove('hidden');
        btnText.textContent = 'Pause Narration';
      } else {
        audio.pause();
        playIcon.classList.remove('hidden');
        pauseIcon.classList.add('hidden');
        btnText.textContent = 'Play Narration';
      }
    };

    audio.addEventListener('ended', () => {
      playIcon.classList.remove('hidden');
      pauseIcon.classList.add('hidden');
      btnText.textContent = 'Play Narration';
    });

    // Speed control buttons
    const speedBtns = document.querySelectorAll('.audio-speed-btn');
    speedBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const speed = parseFloat(btn.getAttribute('data-speed') || '1');
        audio.playbackRate = speed;
        speedBtns.forEach(b => {
          b.classList.remove('bg-blue-600', 'text-white', 'active-speed');
          b.classList.add('bg-gray-200', 'text-gray-700', 'dark:bg-gray-600', 'dark:text-gray-200');
        });
        btn.classList.remove('bg-gray-200', 'text-gray-700', 'dark:bg-gray-600', 'dark:text-gray-200');
        btn.classList.add('bg-blue-600', 'text-white', 'active-speed');
      });
    });

    // Seek slider
    const seekSlider = document.getElementById('audio-seek') as HTMLInputElement | null;
    const currentTimeEl = document.getElementById('audio-current-time');
    const durationEl = document.getElementById('audio-duration');

    const formatTime = (seconds: number) => {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    if (seekSlider && currentTimeEl && durationEl) {
      audio.addEventListener('loadedmetadata', () => {
        seekSlider.max = String(audio.duration);
        durationEl.textContent = formatTime(audio.duration);
      });

      audio.addEventListener('timeupdate', () => {
        if (!seekSlider.dataset.seeking) {
          seekSlider.value = String(audio.currentTime);
          currentTimeEl.textContent = formatTime(audio.currentTime);
        }
      });

      seekSlider.addEventListener('mousedown', () => {
        seekSlider.dataset.seeking = 'true';
      });
      seekSlider.addEventListener('touchstart', () => {
        seekSlider.dataset.seeking = 'true';
      });

      seekSlider.addEventListener('input', () => {
        currentTimeEl.textContent = formatTime(Number(seekSlider.value));
      });

      seekSlider.addEventListener('change', () => {
        audio.currentTime = Number(seekSlider.value);
        delete seekSlider.dataset.seeking;
      });

      audio.addEventListener('ended', () => {
        seekSlider.value = '0';
        currentTimeEl.textContent = '0:00';
      });
    }

    // Voice selector buttons
    const voiceBtns = document.querySelectorAll('.audio-voice-btn');
    voiceBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const voice = btn.getAttribute('data-voice');
        if (!voice) return;
        const wasPlaying = !audio.paused;
        const currentTime = audio.currentTime;
        const currentRate = audio.playbackRate;
        audio.src = `${staticUrl}/audio/${voice}.mp3`;
        audio.playbackRate = currentRate;
        audio.currentTime = currentTime;
        if (seekSlider) seekSlider.value = String(currentTime);
        if (wasPlaying) audio.play();
        voiceBtns.forEach(b => {
          b.classList.remove('bg-blue-600', 'text-white', 'active-voice');
          b.classList.add('bg-gray-200', 'text-gray-700', 'dark:bg-gray-600', 'dark:text-gray-200');
        });
        btn.classList.remove('bg-gray-200', 'text-gray-700', 'dark:bg-gray-600', 'dark:text-gray-200');
        btn.classList.add('bg-blue-600', 'text-white', 'active-voice');
      });
    });
  }, [staticUrl]);

  useEffect(() => {
    // Load static content from static subdomain
    fetch(`${staticUrl}/about.html`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load content: ${response.status}`);
        }
        return response.text();
      })
      .then(data => {
        // Strip <script> tags since they won't execute via dangerouslySetInnerHTML
        const cleaned = data.replace(/<script[\s\S]*?<\/script>/gi, '');
        setContent(cleaned.replaceAll('{{STATIC_URL}}', staticUrl));
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading about content:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [staticUrl]);

  useEffect(() => {
    if (!loading && content) {
      setupAudioNarration();
    }
    return () => {
      delete (window as unknown as Record<string, unknown>).toggleAudioNarration;
    };
  }, [loading, content, setupAudioNarration]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="ui active inline loader"></div>
        <p className="text-gray-600 mt-4">Loading content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <h2 className="text-2xl font-bold mb-2">Error Loading Content</h2>
          <p>{error}</p>
        </div>
        <p className="text-sm text-gray-500">
          Make sure the static content is available at: <code className="bg-gray-100 px-2 py-1 rounded">{staticUrl}/about.html</code>
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <AskAIButton variant="inline" />
      </div>
      <div dangerouslySetInnerHTML={{ __html: content }} />
    </div>
  );
};

export default About;
